import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Plus, Edit, Trash2, Calendar, Clock, AlertCircle, CheckCircle2, User, Upload, Camera, Mail } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function TaskManagement({ siteId, selectedDate, compact = false, onTaskChange }) {
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]); // Unique project names
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [siteManagers, setSiteManagers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAddProjectDialog, setShowAddProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all"); // New site filter
  const [sortOrder, setSortOrder] = useState("status-priority-date"); // New sort order state
  
  // Image capture state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Email task state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailingTask, setEmailingTask] = useState(null);
  const [recipientEmails, setRecipientEmails] = useState("");

  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    scheduled_date: selectedDate || format(new Date(), "yyyy-MM-dd"),
    project: "",
    priority: "medium",
    assigned_to: [],
    site_id: siteId || "",
    images: []
  });

  useEffect(() => {
    if (siteId || compact) {
      loadTasks();
      loadSiteManagers();
      if (siteId) loadProjects();
    }
  }, [siteId, selectedDate, statusFilter, priorityFilter, siteFilter, compact]);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error("Error loading sites:", error);
    }
  };

  const loadTasks = async () => {
    if (!siteId && !compact) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // In compact mode, load tasks from all sites assigned to the user
      if (compact) {
        // Get all sites first
        const sitesResponse = await fetch(`${API}/sites`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        
        if (!sitesResponse.ok) {
          setTasks([]);
          setLoading(false);
          return;
        }
        
        const allSites = await sitesResponse.json();
        
        // Filter sites based on user role
        let userSites = [];
        if (user.role === "admin") {
          userSites = allSites;
        } else {
          // For non-admin users (site_manager, view_only, supervisor), 
          // filter by assigned_sites from user profile
          userSites = allSites.filter(site => 
            user.assigned_sites && user.assigned_sites.includes(site.id)
          );
        }
        
        // Store sites for the dropdown filter
        setSites(userSites);
        
        // Load tasks from all user's sites (or filtered site)
        const sitesToLoad = siteFilter !== "all" 
          ? userSites.filter(site => site.id === siteFilter)
          : userSites;
        
        const allTasks = [];
        for (const site of sitesToLoad) {
          let url;
          // Use general tasks endpoint if filtering by status/priority (ignore selectedDate)
          // This allows filtering by "complete" to show all completed tasks
          if (statusFilter !== "all" || priorityFilter !== "all") {
            const params = new URLSearchParams({ site_id: site.id });
            if (statusFilter && statusFilter !== "all") {
              params.append("status", statusFilter);
            }
            if (priorityFilter && priorityFilter !== "all") {
              params.append("priority", priorityFilter);
            }
            url = `${API}/tasks?${params.toString()}`;
          } else if (selectedDate) {
            // Only use date-based endpoint if no status/priority filters are active
            url = `${API}/tasks/by-date/${selectedDate}?site_id=${site.id}&include_incomplete=true`;
          } else {
            const params = new URLSearchParams({ site_id: site.id });
            url = `${API}/tasks?${params.toString()}`;
          }
          
          const response = await fetch(url, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            let siteTasks = [];
            
            // Check if data has scheduled_tasks/overdue_tasks (date-based endpoint)
            if (data.scheduled_tasks || data.overdue_tasks) {
              // Combine scheduled and overdue tasks
              siteTasks = [...(data.scheduled_tasks || []), ...(data.overdue_tasks || [])];
            } else {
              // Direct array from general tasks endpoint
              siteTasks = data;
            }
            
            // Add site name and site id to each task
            siteTasks.forEach(task => {
              task.siteName = site.name;
              task.siteId = site.id;
            });
            
            allTasks.push(...siteTasks);
          }
        }
        
        // Sort tasks by site name, then by priority, then by date
        allTasks.sort((a, b) => {
          // First by site name
          if (a.siteName !== b.siteName) {
            return a.siteName.localeCompare(b.siteName);
          }
          // Then by priority (high > medium > low)
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          if (a.priority !== b.priority) {
            return priorityOrder[a.priority] - priorityOrder[b.priority];
          }
          // Finally by scheduled date
          return new Date(a.scheduled_date) - new Date(b.scheduled_date);
        });
        
        setTasks(allTasks);
      } else {
        // Original logic for non-compact mode (single site)
        let url;
        if (selectedDate) {
          url = `${API}/tasks/by-date/${selectedDate}?site_id=${siteId}&include_incomplete=true`;
        } else {
          const params = new URLSearchParams({ site_id: siteId });
          if (statusFilter && statusFilter !== "all") {
            params.append("status", statusFilter);
          }
          if (priorityFilter && priorityFilter !== "all") {
            params.append("priority", priorityFilter);
          }
          url = `${API}/tasks?${params.toString()}`;
        }
          
        const response = await fetch(url, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (selectedDate) {
            // Combine scheduled and overdue tasks
            let allTasks = [...(data.scheduled_tasks || []), ...(data.overdue_tasks || [])];
            
            // Apply filters for selected date view
            if (statusFilter && statusFilter !== "all") {
              allTasks = allTasks.filter(task => task.status === statusFilter);
            }
            if (priorityFilter && priorityFilter !== "all") {
              allTasks = allTasks.filter(task => task.priority === priorityFilter);
            }
            
            setTasks(allTasks);
          } else {
            setTasks(data);
          }
        } else {
          console.error("Failed to load tasks");
          setTasks([]);
        }
      }
    } catch (error) {
      console.error("Error loading tasks:", error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSiteManagers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/users/site-managers`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const managers = await response.json();
        setSiteManagers(managers);
      }
    } catch (error) {
      console.error("Error loading site managers:", error);
    }
  };

  const loadProjects = async () => {
    if (!siteId) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/tasks?site_id=${siteId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const allTasks = await response.json();
        // Extract unique project names (filter out empty strings)
        const uniqueProjects = [...new Set(
          allTasks
            .map(task => task.project)
            .filter(project => project && project.trim() !== "")
        )].sort();
        setProjects(uniqueProjects);
      }
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) {
      toast.error("Task title is required");
      return;
    }

    if (!siteId && !newTask.site_id) {
      toast.error("Please select a site");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/tasks`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...newTask,
          site_id: siteId || newTask.site_id,
          assigned_to: [user.id]  // Automatically assign to current user (must be array)
        })
      });

      if (response.ok) {
        const createdTask = await response.json();
        
        // Add site name to the created task
        const taskSite = sites.find(s => s.id === createdTask.site_id);
        if (taskSite) {
          createdTask.siteName = taskSite.name;
          createdTask.siteId = taskSite.id;
        }
        
        setTasks(prev => [...prev, createdTask]);
        toast.success("Task created successfully!");
        setShowAddDialog(false);
        setNewTask({
          title: "",
          description: "",
          scheduled_date: selectedDate || format(new Date(), "yyyy-MM-dd"),
          project: "",
          priority: "medium",
          assigned_to: [],
          site_id: siteId || "",
          images: []
        });
        // Reload projects to include the new one
        loadProjects();
        // Trigger callback to refresh parent
        if (onTaskChange) onTaskChange();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (which can be arrays)
        let errorMessage = "Failed to create task";
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors are arrays of objects
            errorMessage = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(", ");
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedTask = await response.json();
        
        // Add site name to the updated task
        const taskSite = sites.find(s => s.id === updatedTask.site_id);
        if (taskSite) {
          updatedTask.siteName = taskSite.name;
          updatedTask.siteId = taskSite.id;
        }
        
        setTasks(prev => prev.map(task => 
          task.id === taskId ? updatedTask : task
        ));
        
        // Reload projects if project was updated
        if (updates.project !== undefined) {
          loadProjects();
        }
        
        if (updates.status === "complete") {
          toast.success("Task marked as complete!");
        } else if (updates.status === "incomplete") {
          toast.success("Task marked as incomplete");
        } else {
          toast.success("Task updated successfully!");
        }
        
        // Trigger callback to refresh parent
        if (onTaskChange) onTaskChange();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (which can be arrays)
        let errorMessage = "Failed to update task";
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors are arrays of objects
            errorMessage = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(", ");
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        setTasks(prev => prev.filter(task => task.id !== taskId));
        toast.success("Task deleted successfully!");
        
        // Close edit dialog if open
        setShowEditDialog(false);
        setEditingTask(null);
        
        // Trigger callback to refresh parent
        if (onTaskChange) onTaskChange();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (which can be arrays)
        let errorMessage = "Failed to delete task";
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(", ");
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const toggleTaskStatus = (task) => {
    const newStatus = task.status === "complete" ? "incomplete" : "complete";
    handleUpdateTask(task.id, { status: newStatus });
  };

  // Image handling functions
  const resizeImage = (base64Str, maxWidth = 800, maxHeight = 800) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
    });
  };

  const handleImageUpload = async (e, isEditing = false) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const resized = await resizeImage(reader.result);
        if (isEditing) {
          setEditingTask(prev => ({
            ...prev,
            images: [...(prev.images || []), resized].slice(0, 3)
          }));
        } else {
          setNewTask(prev => ({
            ...prev,
            images: [...(prev.images || []), resized].slice(0, 3)
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async (isEditing = false) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setVideoStream(stream);
      setIsCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error("Could not access camera");
    }
  };

  const stopCamera = () => {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
      setIsCameraActive(false);
    }
  };

  const capturePhoto = async (isEditing = false) => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.7);
      const resized = await resizeImage(imageData);
      
      if (isEditing) {
        setEditingTask(prev => ({
          ...prev,
          images: [...(prev.images || []), resized].slice(0, 3)
        }));
      } else {
        setNewTask(prev => ({
          ...prev,
          images: [...(prev.images || []), resized].slice(0, 3)
        }));
      }
      
      stopCamera();
      toast.success("Photo captured!");
    }
  };

  const removeImage = (index, isEditing = false) => {
    if (isEditing) {
      setEditingTask(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    } else {
      setNewTask(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const generateTaskEmail = (task) => {
    const subject = `Task Assignment: ${task.title}`;
    const completionLink = `${window.location.origin}/task-complete/${task.id}/${task.completion_token || 'token'}`;
    const hasPhotos = task.images && task.images.length > 0;
    
    const body = `Hello,

${user.username} has requested the following task be completed as soon as possible:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TASK DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Title: ${task.title}

Site: ${task.siteName || 'N/A'}

Description: ${task.description || 'No description provided'}

Scheduled Date: ${format(new Date(task.scheduled_date), "dd MMM yyyy")}

Priority: ${task.priority.toUpperCase()}

Project: ${task.project || 'N/A'}

${hasPhotos ? `\n📸 PHOTOS ATTACHED: ${task.images.length} photo(s) included with this task\n   (View photos by clicking the link below)\n` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

VIEW FULL TASK & MARK AS COMPLETE:

Click the link below to view complete task details${hasPhotos ? ' with photos' : ''} and mark it as complete:

${completionLink}

No login required! Simply:
1. Click the link above to view${hasPhotos ? ' photos and' : ''} full details
2. Enter your email address
3. Click "Mark Task Complete"

You will receive a confirmation, and ${user.username} will be automatically notified.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Task Requester: ${user.username}
Contact Email: ${user.email}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This is an automated message from SiteProApp.
${hasPhotos ? '\n📌 Note: This task includes photo attachments. Click the link above to view them.' : ''}
`;

    return { subject, body, completionLink };
  };

  const handleEmailTask = (task) => {
    console.log("handleEmailTask called", task);
    
    // Temporary simple prompt for email address
    const email = prompt("Enter recipient email address(s) (comma-separated):");
    
    if (email && email.trim()) {
      setRecipientEmails(email);
      setEmailingTask(task);
      
      // Generate and send email immediately
      const { subject, body } = generateTaskEmail(task);
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
      
      // Send copy to user
      setTimeout(() => {
        const copySubject = `[Copy] ${subject}`;
        const copyBody = `This is a copy of the task email you sent.\n\n${body}`;
        const copyMailto = `mailto:${user.email}?subject=${encodeURIComponent(copySubject)}&body=${encodeURIComponent(copyBody)}`;
        window.open(copyMailto, '_blank');
      }, 500);
      
      toast.success("Email client opened! Please send the emails.");
    }
  };

  const sendTaskEmail = () => {
    if (!recipientEmails.trim()) {
      toast.error("Please enter at least one email address");
      return;
    }

    const { subject, body } = generateTaskEmail(emailingTask);
    
    // Create mailto link
    const mailtoLink = `mailto:${recipientEmails}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Send confirmation copy to logged-in user
    setTimeout(() => {
      const copySubject = `[Copy] ${subject}`;
      const copyBody = `This is a copy of the task email you sent.\n\n${body}`;
      const copyMailto = `mailto:${user.email}?subject=${encodeURIComponent(copySubject)}&body=${encodeURIComponent(copyBody)}`;
      window.open(copyMailto, '_blank');
    }, 500);
    
    toast.success("Email client opened! Please send the email.");
    setShowEmailDialog(false);
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.name : "Unknown Site";
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800 border-red-200";
      case "medium": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "complete": return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case "in_progress": return <Clock className="w-4 h-4 text-blue-600" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const isOverdue = (task) => {
    if (task.status === "complete") return false;
    return task.scheduled_date < format(new Date(), "yyyy-MM-dd");
  };

  if (compact) {
    // Compact view for daily form - Table format with filters
    return (
      <Card className="border-2 border-blue-700">
        <CardHeader className="bg-blue-700 text-white p-2 flex flex-row items-center justify-between">
          <CardTitle className="text-xs font-bold">TASKS & ACTION ITEMS</CardTitle>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-white hover:bg-blue-600"
            onClick={() => setShowAddDialog(true)}
          >
            <Plus className="w-3 h-3" />
          </Button>
        </CardHeader>
        <CardContent className="p-2">
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            <div>
              <Label className="text-xs">Site</Label>
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sort Order Selector */}
          <div className="mb-3 border-t pt-2">
            <Label className="text-xs font-semibold mb-1 block">Sort By:</Label>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="status-priority-date">Status → Priority → Due Date</SelectItem>
                <SelectItem value="priority-status-date">Priority → Status → Due Date</SelectItem>
                <SelectItem value="date-asc">Due Date (Earliest First)</SelectItem>
                <SelectItem value="date-desc">Due Date (Latest First)</SelectItem>
                <SelectItem value="priority-date">Priority → Due Date</SelectItem>
                <SelectItem value="status-date">Status → Due Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-xs text-gray-500">Loading tasks...</div>
          ) : tasks.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-4">
              No tasks found. Click + to add a task.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {tasks.sort((a, b) => {
                const statusOrder = { incomplete: 0, in_progress: 1, complete: 2 };
                const priorityOrder = { high: 0, medium: 1, low: 2 };
                
                switch (sortOrder) {
                  case "status-priority-date":
                    // Status → Priority → Due Date
                    const statusDiff1 = statusOrder[a.status] - statusOrder[b.status];
                    if (statusDiff1 !== 0) return statusDiff1;
                    const priorityDiff1 = priorityOrder[a.priority] - priorityOrder[b.priority];
                    if (priorityDiff1 !== 0) return priorityDiff1;
                    return a.scheduled_date.localeCompare(b.scheduled_date);
                  
                  case "priority-status-date":
                    // Priority → Status → Due Date
                    const priorityDiff2 = priorityOrder[a.priority] - priorityOrder[b.priority];
                    if (priorityDiff2 !== 0) return priorityDiff2;
                    const statusDiff2 = statusOrder[a.status] - statusOrder[b.status];
                    if (statusDiff2 !== 0) return statusDiff2;
                    return a.scheduled_date.localeCompare(b.scheduled_date);
                  
                  case "date-asc":
                    // Due Date (Earliest First)
                    return a.scheduled_date.localeCompare(b.scheduled_date);
                  
                  case "date-desc":
                    // Due Date (Latest First)
                    return b.scheduled_date.localeCompare(a.scheduled_date);
                  
                  case "priority-date":
                    // Priority → Due Date
                    const priorityDiff3 = priorityOrder[a.priority] - priorityOrder[b.priority];
                    if (priorityDiff3 !== 0) return priorityDiff3;
                    return a.scheduled_date.localeCompare(b.scheduled_date);
                  
                  case "status-date":
                    // Status → Due Date
                    const statusDiff3 = statusOrder[a.status] - statusOrder[b.status];
                    if (statusDiff3 !== 0) return statusDiff3;
                    return a.scheduled_date.localeCompare(b.scheduled_date);
                  
                  default:
                    return 0;
                }
              }).map((task) => {
                const overdue = isOverdue(task);
                const complete = task.status === "complete";
                
                // Sticky note colors based on priority
                const stickyColor = complete 
                  ? "bg-green-100 border-green-300"
                  : overdue
                  ? "bg-red-100 border-red-300"
                  : task.priority === "high"
                  ? "bg-yellow-100 border-yellow-300"
                  : task.priority === "medium"
                  ? "bg-blue-100 border-blue-300"
                  : "bg-purple-100 border-purple-300";
                
                return (
                  <div
                    key={task.id}
                    className={`${stickyColor} border-2 rounded-lg p-3 shadow-md hover:shadow-lg transition-shadow relative`}
                    style={{ minHeight: "160px" }}
                  >
                    {/* Checkbox in top left */}
                    <div className="absolute top-2 left-2">
                      <Checkbox
                        checked={complete}
                        onCheckedChange={() => toggleTaskStatus(task)}
                        className="w-4 h-4"
                      />
                    </div>
                    
                    {/* Action buttons in top right */}
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 hover:bg-white/50"
                        onClick={() => handleEmailTask(task)}
                        title="Email Task"
                      >
                        <Mail className="w-3 h-3" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 hover:bg-white/50"
                        onClick={() => {
                          setEditingTask(task);
                          setShowEditDialog(true);
                        }}
                        title="Edit Task"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                    </div>
                    
                    {/* Task content */}
                    <div className="mt-6 space-y-2">
                      {/* Site badge */}
                      {task.siteName && (
                        <Badge className="text-[10px] sm:text-xs bg-slate-700 text-white mb-2">
                          {task.siteName}
                        </Badge>
                      )}
                      
                      {/* Title */}
                      <h3 className={`font-bold text-xs sm:text-sm break-words ${complete ? "line-through text-gray-600" : "text-gray-900"}`}>
                        {task.title}
                      </h3>
                      
                      {/* Description */}
                      {task.description && (
                        <p className="text-[10px] sm:text-xs text-gray-700 line-clamp-2 break-words">
                          {task.description}
                        </p>
                      )}
                      
                      {/* Project */}
                      {task.project && (
                        <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-600">
                          <span className="font-semibold shrink-0">Project:</span>
                          <span className="truncate">{task.project}</span>
                        </div>
                      )}
                      
                      {/* Bottom info */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-300 flex-wrap gap-2">
                        <div className="flex items-center gap-1">
                          {getStatusIcon(task.status)}
                          <span className="text-[10px] sm:text-xs capitalize">{task.status.replace('_', ' ')}</span>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 shrink-0" />
                          <span className={`text-[10px] sm:text-xs ${overdue ? "text-red-700 font-bold" : "text-gray-600"}`}>
                            {format(new Date(task.scheduled_date), "dd MMM")}
                          </span>
                        </div>
                      </div>
                      
                      {/* Priority badge */}
                      <div className="flex justify-end">
                        <Badge className={`text-xs px-2 py-0.5 ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </Badge>
                      </div>
                      
                      {/* Task Images */}
                      {task.images && task.images.length > 0 && (
                        <div className="flex gap-1 mt-2 pt-2 border-t border-gray-300">
                          {task.images.map((img, idx) => (
                            <img 
                              key={idx}
                              src={img} 
                              alt={`Task ${idx + 1}`} 
                              className="w-12 h-12 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => window.open(img, '_blank')}
                            />
                          ))}
                        </div>
                      )}
                      
                      {/* Completed date */}
                      {complete && task.completed_at && (
                        <div className="mt-2 pt-2 border-t border-green-400">
                          <div className="flex items-center gap-1 text-xs text-green-800">
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="font-semibold">Completed:</span>
                            <span>{format(new Date(task.completed_at), "dd MMM yyyy")}</span>
                          </div>
                          {task.days_to_complete !== null && task.days_to_complete !== undefined && (
                            <div className="flex items-center gap-1 text-xs text-green-800 mt-1 ml-4">
                              <Clock className="w-3 h-3" />
                              <span className="font-semibold">
                                {task.days_to_complete === 0 
                                  ? "Completed on time" 
                                  : task.days_to_complete > 0 
                                  ? `${task.days_to_complete} day${task.days_to_complete !== 1 ? 's' : ''} late`
                                  : `${Math.abs(task.days_to_complete)} day${Math.abs(task.days_to_complete) !== 1 ? 's' : ''} early`
                                }
                              </span>
                            </div>
                          )}
                          {task.completed_by_name && (
                            <div className="flex items-center gap-1 text-xs text-green-800 mt-1 ml-4">
                              <User className="w-3 h-3" />
                              <span className="font-semibold">By:</span>
                              <span>{task.completed_by_name}</span>
                              {task.completed_by_email && (
                                <span className="text-green-700">({task.completed_by_email})</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Overdue indicator */}
                      {overdue && !complete && (
                        <div className="absolute bottom-2 left-2 text-red-700 text-xs font-bold animate-pulse">
                          ⚠️ OVERDUE
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>

        {/* Add Task Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Add New Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 overflow-y-auto flex-1">
              {!siteId && (
                <div className="space-y-2">
                  <Label htmlFor="task-site">Site *</Label>
                  <Select 
                    value={newTask.site_id || ""} 
                    onValueChange={(value) => setNewTask(prev => ({ ...prev, site_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select site" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="task-title">Task Title *</Label>
                <Input
                  id="task-title"
                  placeholder="Enter task title"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-description">Description</Label>
                <Textarea
                  id="task-description"
                  placeholder="Enter task description (optional)"
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-project">Project</Label>
                <Select 
                  value={newTask.project || "__none__"} 
                  onValueChange={(value) => {
                    if (value === "__add_new__") {
                      setShowAddProjectDialog(true);
                    } else if (value === "__none__") {
                      setNewTask(prev => ({ ...prev, project: "" }));
                    } else {
                      setNewTask(prev => ({ ...prev, project: value }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No Project</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project} value={project}>
                        {project}
                      </SelectItem>
                    ))}
                    <SelectItem value="__add_new__">+ Add New Project</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="task-date">Scheduled Date</Label>
                  <Input
                    id="task-date"
                    type="date"
                    value={newTask.scheduled_date}
                    onChange={(e) => setNewTask(prev => ({ ...prev, scheduled_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="task-priority">Priority</Label>
                  <Select value={newTask.priority} onValueChange={(value) => setNewTask(prev => ({ ...prev, priority: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Image Capture Section */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Task Photos (Up to 3)</Label>
                <div className="flex flex-wrap gap-2">
                  {newTask.images && newTask.images.map((img, idx) => (
                    <div key={idx} className="relative w-20 h-20">
                      <img src={img} alt={`Task ${idx + 1}`} className="w-full h-full object-cover rounded border" />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full"
                        onClick={() => removeImage(idx, false)}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
                {(!newTask.images || newTask.images.length < 3) && (
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <div className="flex items-center gap-2 px-3 py-2 border rounded hover:bg-gray-50">
                        <Upload className="w-4 h-4" />
                        <span className="text-sm">Upload</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, false)}
                      />
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => startCamera(false)}
                      className="flex items-center gap-2"
                    >
                      <Camera className="w-4 h-4" />
                      Camera
                    </Button>
                  </div>
                )}
                
                {/* Camera View */}
                {isCameraActive && (
                  <div className="space-y-2 p-4 border rounded bg-gray-50">
                    <video ref={videoRef} autoPlay playsInline className="w-full rounded" />
                    <canvas ref={canvasRef} className="hidden" />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => capturePhoto(false)}
                        className="flex-1"
                      >
                        Capture Photo
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={stopCamera}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Assign To (Optional)</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {siteManagers.map((manager) => (
                    <div key={manager.id} className="flex items-center space-x-2">
                      <Checkbox
                        checked={newTask.assigned_to.includes(manager.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setNewTask(prev => ({
                              ...prev,
                              assigned_to: [...prev.assigned_to, manager.id]
                            }));
                          } else {
                            setNewTask(prev => ({
                              ...prev,
                              assigned_to: prev.assigned_to.filter(id => id !== manager.id)
                            }));
                          }
                        }}
                      />
                      <Label className="text-sm">{manager.username}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter className="flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddDialog(false);
                  setNewTask({
                    title: "",
                    description: "",
                    scheduled_date: selectedDate || format(new Date(), "yyyy-MM-dd"),
                    project: "",
                    priority: "medium",
                    assigned_to: [],
                    site_id: siteId || "",
                    images: []
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddTask}
                disabled={saving || !newTask.title.trim()}
              >
                {saving ? "Creating..." : "Create Task"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Task Dialog */}
        {editingTask && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Edit Task</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4 overflow-y-auto flex-1">
                <div className="space-y-2">
                  <Label htmlFor="edit-task-title">Task Title *</Label>
                  <Input
                    id="edit-task-title"
                    value={editingTask.title}
                    onChange={(e) => setEditingTask(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                {!siteId && (
                  <div className="space-y-2">
                    <Label htmlFor="edit-task-site">Site *</Label>
                    <Select 
                      value={editingTask.site_id || ""} 
                      onValueChange={(value) => setEditingTask(prev => ({ ...prev, site_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select site" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="edit-task-description">Description</Label>
                  <Textarea
                    id="edit-task-description"
                    value={editingTask.description || ""}
                    onChange={(e) => setEditingTask(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-task-project">Project</Label>
                  <Select 
                    value={editingTask.project || "__none__"} 
                    onValueChange={(value) => {
                      if (value === "__add_new__") {
                        setShowAddProjectDialog(true);
                      } else if (value === "__none__") {
                        setEditingTask(prev => ({ ...prev, project: "" }));
                      } else {
                        setEditingTask(prev => ({ ...prev, project: value }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select project (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No Project</SelectItem>
                      {projects.map((project) => (
                        <SelectItem key={project} value={project}>
                          {project}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_new__">+ Add New Project</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-task-date">Scheduled Date</Label>
                    <Input
                      id="edit-task-date"
                      type="date"
                      value={editingTask.scheduled_date}
                      onChange={(e) => setEditingTask(prev => ({ ...prev, scheduled_date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-task-priority">Priority</Label>
                    <Select value={editingTask.priority} onValueChange={(value) => setEditingTask(prev => ({ ...prev, priority: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-task-status">Status</Label>
                  <Select value={editingTask.status} onValueChange={(value) => setEditingTask(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="incomplete">Incomplete</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Completion Information - Only show if task is completed */}
                {editingTask.status === "complete" && (
                  <div className="space-y-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800 font-semibold">
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Task Completed</span>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="edit-completion-date" className="text-sm text-gray-700">Completion Date *</Label>
                        <Input
                          id="edit-completion-date"
                          type="date"
                          value={editingTask.completed_at ? format(new Date(editingTask.completed_at), "yyyy-MM-dd") : ""}
                          onChange={(e) => {
                            if (e.target.value) {
                              // Update completion date
                              const completionDate = new Date(e.target.value);
                              setEditingTask(prev => ({ 
                                ...prev, 
                                completed_at: completionDate.toISOString()
                              }));
                              
                              // Recalculate days_to_complete
                              const scheduledDate = new Date(editingTask.scheduled_date);
                              const daysDiff = Math.floor((completionDate - scheduledDate) / (1000 * 60 * 60 * 24));
                              setEditingTask(prev => ({
                                ...prev,
                                days_to_complete: daysDiff
                              }));
                            }
                          }}
                          className="bg-white"
                        />
                      </div>
                      {editingTask.days_to_complete !== null && editingTask.days_to_complete !== undefined && (
                        <div className="p-3 bg-white rounded border border-green-300">
                          <Label className="text-xs text-gray-600">Performance</Label>
                          <div className={`text-lg font-semibold mt-1 ${
                            editingTask.days_to_complete === 0 
                              ? "text-green-700" 
                              : editingTask.days_to_complete > 0 
                              ? "text-red-700"
                              : "text-blue-700"
                          }`}>
                            {editingTask.days_to_complete === 0 
                              ? "✓ Completed On Time" 
                              : editingTask.days_to_complete > 0 
                              ? `${editingTask.days_to_complete} day${editingTask.days_to_complete !== 1 ? 's' : ''} late`
                              : `${Math.abs(editingTask.days_to_complete)} day${Math.abs(editingTask.days_to_complete) !== 1 ? 's' : ''} early`
                            }
                          </div>
                        </div>
                      )}
                      {(editingTask.completed_by_name || editingTask.completed_by_email) && (
                        <div className="p-3 bg-white rounded border border-green-300">
                          <Label className="text-xs text-gray-600 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Completed By
                          </Label>
                          <div className="space-y-1 mt-1">
                            {editingTask.completed_by_name && (
                              <div className="text-sm font-medium text-green-800">
                                {editingTask.completed_by_name}
                              </div>
                            )}
                            {editingTask.completed_by_email && (
                              <div className="text-xs text-green-700">
                                {editingTask.completed_by_email}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Image Capture Section for Edit */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Task Photos (Up to 3)</Label>
                  <div className="flex flex-wrap gap-2">
                    {editingTask.images && editingTask.images.map((img, idx) => (
                      <div key={idx} className="relative w-20 h-20">
                        <img src={img} alt={`Task ${idx + 1}`} className="w-full h-full object-cover rounded border" />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full"
                          onClick={() => removeImage(idx, true)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                  {(!editingTask.images || editingTask.images.length < 3) && (
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <div className="flex items-center gap-2 px-3 py-2 border rounded hover:bg-gray-50">
                          <Upload className="w-4 h-4" />
                          <span className="text-sm">Upload</span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, true)}
                        />
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => startCamera(true)}
                        className="flex items-center gap-2"
                      >
                        <Camera className="w-4 h-4" />
                        Camera
                      </Button>
                    </div>
                  )}
                  
                  {/* Camera View for Edit */}
                  {isCameraActive && (
                    <div className="space-y-2 p-4 border rounded bg-gray-50">
                      <video ref={videoRef} autoPlay playsInline className="w-full rounded" />
                      <canvas ref={canvasRef} className="hidden" />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          onClick={() => capturePhoto(true)}
                          className="flex-1"
                        >
                          Capture Photo
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={stopCamera}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="flex-shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDeleteTask(editingTask.id)}
                  className="mr-auto text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingTask(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={async () => {
                    const updateData = {
                      title: editingTask.title,
                      description: editingTask.description,
                      scheduled_date: editingTask.scheduled_date,
                      project: editingTask.project,
                      priority: editingTask.priority,
                      status: editingTask.status,
                      site_id: editingTask.site_id,
                      images: editingTask.images || []
                    };
                    
                    // Include completion data if task is completed
                    if (editingTask.status === "complete" && editingTask.completed_at) {
                      updateData.completed_at = editingTask.completed_at;
                      updateData.days_to_complete = editingTask.days_to_complete;
                    }
                    
                    await handleUpdateTask(editingTask.id, updateData);
                    setShowEditDialog(false);
                    setEditingTask(null);
                  }}
                  disabled={!editingTask.title.trim()}
                >
                  Update Task
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        
        {/* Add New Project Dialog */}
        <Dialog open={showAddProjectDialog} onOpenChange={setShowAddProjectDialog}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Add New Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-project-name">Project Name *</Label>
                <Input
                  id="new-project-name"
                  placeholder="Enter project name"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter className="flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddProjectDialog(false);
                  setNewProjectName("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (newProjectName.trim()) {
                    // Add to projects list and select it
                    setProjects(prev => [...new Set([...prev, newProjectName.trim()])].sort());
                    
                    // Set the new project in the active form
                    if (showAddDialog) {
                      setNewTask(prev => ({ ...prev, project: newProjectName.trim() }));
                    } else if (editingTask) {
                      setEditingTask(prev => ({ ...prev, project: newProjectName.trim() }));
                    }
                    
                    setShowAddProjectDialog(false);
                    setNewProjectName("");
                    toast.success("Project added successfully!");
                  }
                }}
                disabled={!newProjectName.trim()}
              >
                Add Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    );
  }

  // Full task management view (for dedicated task management page)
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Task Management</h2>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading tasks...</div>
      ) : (
        <div className="grid gap-4">
          {tasks.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <p className="text-gray-500">No tasks found. Create your first task!</p>
              </CardContent>
            </Card>
          ) : (
            tasks.map((task) => (
              <Card key={task.id} className={`${task.status === "complete" ? "opacity-75" : ""}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <Checkbox
                        checked={task.status === "complete"}
                        onCheckedChange={() => toggleTaskStatus(task)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        {compact && task.siteName && (
                          <Badge className="mb-2 bg-slate-600 text-white">
                            {task.siteName}
                          </Badge>
                        )}
                        <h3 className={`font-semibold ${task.status === "complete" ? "line-through text-gray-500" : ""}`}>
                          {task.title}
                        </h3>
                        {task.description && (
                          <p className="text-gray-600 mt-1">{task.description}</p>
                        )}
                        <div className="flex items-center space-x-4 mt-2">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{task.scheduled_date}</span>
                          </div>
                          <Badge className={getPriorityColor(task.priority)}>
                            {task.priority}
                          </Badge>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(task.status)}
                            <span className="text-sm text-gray-600 capitalize">{task.status.replace('_', ' ')}</span>
                          </div>
                          {isOverdue(task) && (
                            <Badge className="bg-red-100 text-red-800">Overdue</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingTask(task);
                          setShowEditDialog(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteTask(task.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
      
      {/* Email Task Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Email Task Assignment</DialogTitle>
            <DialogDescription>
              Send this task to someone via email with a completion link
            </DialogDescription>
          </DialogHeader>
          
          {emailingTask && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded border">
                <h4 className="font-semibold text-sm mb-2">{emailingTask.title}</h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <p><strong>Site:</strong> {emailingTask.siteName || 'N/A'}</p>
                  <p><strong>Due:</strong> {format(new Date(emailingTask.scheduled_date), "dd MMM yyyy")}</p>
                  <p><strong>Priority:</strong> {emailingTask.priority.toUpperCase()}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipient-emails">Recipient Email(s) *</Label>
                <Input
                  id="recipient-emails"
                  type="text"
                  placeholder="email@example.com, another@example.com"
                  value={recipientEmails}
                  onChange={(e) => setRecipientEmails(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  Enter one or more email addresses separated by commas
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                <p className="font-semibold text-blue-900 mb-1">What will be sent:</p>
                <ul className="text-blue-800 space-y-1 text-xs">
                  <li>✓ Task details and instructions</li>
                  <li>✓ Clickable "Mark Complete" link (no login required)</li>
                  <li>✓ Copy of email sent to you ({user.email})</li>
                  <li>✓ You'll be notified when task is marked complete</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={sendTaskEmail} className="bg-blue-600 hover:bg-blue-700">
              <Mail className="w-4 h-4 mr-2" />
              Open Email Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}